/**
 * 표현 리스크(/compliance/text-check 등) ApiResponse 본문을 화면 표시용으로 정규화한다.
 * - axios `response.data` 또는 이미 꺼낸 payload 모두 허용
 * - `{ success, data: { ... } }` 형태를 한 번 이상 벗겨 실제 페이로드까지 진입
 * - 스네이크 케이스(`safe_rewrite`, `risk_level`) 폴백
 */

function pickFirst(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined && obj[k] !== null) {
      return obj[k]
    }
  }
  return undefined
}

function isCompliancePayload(o) {
  if (!o || typeof o !== 'object') return false
  return (
    Object.prototype.hasOwnProperty.call(o, 'safeRewrite')
    || Object.prototype.hasOwnProperty.call(o, 'safe_rewrite')
    || Object.prototype.hasOwnProperty.call(o, 'findings')
    || Object.prototype.hasOwnProperty.call(o, 'riskLevel')
    || Object.prototype.hasOwnProperty.call(o, 'risk_level')
    || Object.prototype.hasOwnProperty.call(o, 'score')
    || Object.prototype.hasOwnProperty.call(o, 'checklist')
    || Object.prototype.hasOwnProperty.call(o, 'check_list')
  )
}

function normalizeFinding(f) {
  if (!f || typeof f !== 'object') {
    return { keyword: '', category: '', reason: '', suggestion: '' }
  }
  const keyword = String(pickFirst(f, ['keyword']) ?? '')
  const category = String(pickFirst(f, ['category']) ?? '')
  const reason = String(pickFirst(f, ['reason']) ?? '')
  const suggestion = String(pickFirst(f, ['suggestion']) ?? '')
  return { keyword, category, reason, suggestion }
}

/**
 * @param {unknown} raw axios 응답의 `.data` 또는 unwrap 된 객체
 * @returns {{ riskLevel: string, score: number, findings: Array, safeRewrite: string, checklist: string[] } | null}
 */
export function normalizeComplianceCheckResult(raw) {
  let o = raw
  if (o == null) return null
  if (typeof o === 'string') {
    try {
      o = JSON.parse(o)
    } catch {
      return null
    }
  }
  for (let depth = 0; depth < 12; depth++) {
    if (!o || typeof o !== 'object') return null
    if (isCompliancePayload(o)) break
    const inner = pickFirst(o, ['data', 'payload', 'result'])
    if (inner === undefined) break
    if (typeof inner === 'string') {
      try {
        o = JSON.parse(inner)
      } catch {
        return null
      }
      continue
    }
    o = inner
  }
  if (!o || typeof o !== 'object' || !isCompliancePayload(o)) return null

  const safeRewriteRaw = pickFirst(o, ['safeRewrite', 'safe_rewrite'])
  const safeRewrite = safeRewriteRaw == null ? '' : String(safeRewriteRaw)

  const riskLevel = String(pickFirst(o, ['riskLevel', 'risk_level']) ?? 'LOW')

  const scoreRaw = pickFirst(o, ['score'])
  const score = typeof scoreRaw === 'number' && !Number.isNaN(scoreRaw) ? scoreRaw : Number(scoreRaw) || 0

  const findingsRaw = pickFirst(o, ['findings'])
  const findings = Array.isArray(findingsRaw) ? findingsRaw.map(normalizeFinding) : []

  const checklistRaw = pickFirst(o, ['checklist', 'check_list'])
  const checklist = Array.isArray(checklistRaw) ? checklistRaw.map((x) => String(x ?? '')) : []

  return {
    riskLevel,
    score,
    findings,
    safeRewrite,
    checklist,
  }
}
